import { FC, JSX, useContext } from "react";
import { Box, Flex } from "@otrium/core";
import { FormikErrors, FormikProps, FormikTouched } from "formik";
import { useLingui } from "@lingui/react";
import { t } from "@lingui/macro";
import { I18n } from "@lingui/core";
import { Iso2LetterLocale, StoreKey } from "lib/Intl";
import { AppCtx } from "src/contexts/app.context";
import { Checkout } from "src/organisms/CheckoutView/CheckoutStepInfo";
import { Profile } from "src/molecules/ProfileForm/ProfileForm";
import {
  countryOptions,
  checkoutCOMCountryOptions,
  usStateOptions,
} from "src/utils/regionName/countries";
import { FieldWithError } from "src/molecules/FieldWithError";
import { useAutompleteAddressNl } from "src/hooks/useAutocompleteAddressNl";

type FieldKey =
  | "streetName"
  | "streetNumber"
  | "additionalStreetInfo"
  | "postalCode"
  | "city"
  | "state"
  | "country"
  | "isStreetNumberNotRequired";

type Field = {
  type: "input" | "dropdown" | "checkbox";
  dataTestId: string;
  name: FieldKey;
  label: string;
  disabled: boolean;
  autoComplete?: string;
  options?: {
    key: string;
    text: string;
    value: string;
  }[];
};

type Fields = {
  [key in FieldKey]: Field;
};

type FormSubRow = (FieldKey | null)[];

type FormRow = (FieldKey | FormSubRow | null)[];

export interface Address {
  streetName: string;
  streetNumber: string;
  additionalStreetInfo: string;
  postalCode: string;
  city: string;
  state: string;
  country: Iso2LetterLocale;
}

const getFields = (
  i18n: I18n,
  formik: FormikProps<Checkout> | FormikProps<Profile>,
  type: "checkout" | "profile",
  addressType: "shippingAddress" | "billingAddress",
  storeKey: StoreKey
): Fields => {
  const isCheckout = type === "checkout";
  const isCOMStoreKey = storeKey === "OT-COM";

  const isStreetNumberNotRequired =
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    formik.values[addressType].isStreetNumberNotRequired as boolean;
  return {
    streetName: {
      type: "input",
      dataTestId: `${type}-address-form-street-name`,
      name: "streetName",
      label: `${i18n._(t`Street`)} *`,
      disabled: false,
    },
    streetNumber: {
      type: "input",
      dataTestId: `${type}-address-form-street-number`,
      name: "streetNumber",
      label: `${i18n._(t`Number`)}${isStreetNumberNotRequired ? "" : " *"}`,
      disabled: false,
      autoComplete: "force-off",
    },
    additionalStreetInfo: {
      type: "input",
      dataTestId: `${type}-address-form-additional-street-info`,
      name: "additionalStreetInfo",
      label: `${i18n._(t`Addition`)}`,
      disabled: false,
      autoComplete: "force-off",
    },
    postalCode: {
      type: "input",
      dataTestId: `${type}-address-form-postal-code`,
      name: "postalCode",
      label: `${i18n._(t`Postcode`)} *`,
      disabled: false,
    },
    city: {
      type: "input",
      dataTestId: `${type}-address-form-city`,
      name: "city",
      label: `${i18n._(t`City`)} *`,
      disabled: false,
    },
    state: {
      type: "dropdown",
      dataTestId: `${type}-address-form-state`,
      name: "state",
      label: `${i18n._(t`State`)} *`,
      disabled: false,
      options: usStateOptions,
    },
    country: {
      type: "dropdown",
      dataTestId: `${type}-address-form-country`,
      name: "country",
      label: `${i18n._(t`Country`)} *`,
      disabled: !isCOMStoreKey,
      options:
        isCheckout && isCOMStoreKey
          ? checkoutCOMCountryOptions
          : countryOptions,
    },
    isStreetNumberNotRequired: {
      type: "checkbox",
      dataTestId: `${type}-address-form-is-street-number-not-required`,
      name: "isStreetNumberNotRequired",
      label: i18n._(t`My address does not contain a street number`),
      disabled: false,
    },
  };
};

const getFormRows = (
  country: string,
  showAutocompleteAddressNlForm: boolean
): FormRow[] => {
  let formRows;
  if (country === "US") {
    formRows = [
      ["streetName", "additionalStreetInfo"],
      ["city", "state"],
      ["postalCode", "country"],
    ];
  } else if (country === "FR") {
    formRows = [
      ["streetNumber", "streetName"],
      ["isStreetNumberNotRequired"],
      ["postalCode", "city"],
      ["country", null],
    ];
  } else if (country === "NL") {
    formRows = showAutocompleteAddressNlForm
      ? [
          ["postalCode", "city"],
          ["streetName", ["streetNumber", "additionalStreetInfo"]],
          ["country", null],
        ]
      : [["postalCode", "streetNumber"]];
  } else {
    formRows = [
      ["streetName", ["streetNumber", "additionalStreetInfo"]],
      ["postalCode", "city"],
      ["country", null],
    ];
  }
  return formRows as FormRow[];
};

interface FormSubRowProps {
  formSubRow: FormSubRow;
  i18n: I18n;
  formik: FormikProps<Checkout> | FormikProps<Profile>;
  type: "checkout" | "profile";
  addressType: "shippingAddress" | "billingAddress";
}

const FormSubRow: FC<FormSubRowProps> = ({
  formSubRow,
  i18n,
  formik,
  type,
  addressType,
}) => {
  const { storeKey } = useContext(AppCtx);
  const fields = getFields(i18n, formik, type, addressType, storeKey);
  return (
    <Flex>
      {formSubRow.map((formSubRowItem: FieldKey | null, index: number) => (
        <Box key={index} flex="1" {...(index === 0 ? { mr: 1 } : { ml: 1 })}>
          {formSubRowItem && (
            <FieldWithError
              formik={formik}
              type={fields[formSubRowItem].type}
              formType={type}
              data-testid={fields[formSubRowItem].dataTestId}
              label={fields[formSubRowItem].label}
              field={`${addressType}.${fields[formSubRowItem].name}`}
              disabled={fields[formSubRowItem].disabled}
              options={fields[formSubRowItem].options}
              autoComplete={fields[formSubRowItem].autoComplete}
            />
          )}
        </Box>
      ))}
    </Flex>
  );
};

interface FormRowProps {
  formRow: FormRow;
  formRowIndex: number;
  i18n: I18n;
  formik: FormikProps<Checkout> | FormikProps<Profile>;
  type: "checkout" | "profile";
  addressType: "shippingAddress" | "billingAddress";
}

const FormRow: FC<FormRowProps> = ({
  formRow,
  formRowIndex,
  i18n,
  formik,
  type,
  addressType,
}) => {
  const { storeKey } = useContext(AppCtx);
  const fields = getFields(i18n, formik, type, addressType, storeKey);
  return (
    <Flex flexDirection={["column", "column", "column", "row"]}>
      {formRow.map(
        (formRowItem: FieldKey | FormSubRow | null, index: number) => {
          const isFieldExists = !!formRowItem;
          const isField = typeof formRowItem === "string";
          return (
            <Box
              key={index}
              flex="1"
              {...{
                mt: isFieldExists ? (formRowIndex === 0 ? [4, 4, 4, 0] : 4) : 0,
              }}
              {...(index === 1 ? { ml: [0, 0, 0, 4] } : {})}
            >
              {isFieldExists &&
                (isField ? (
                  <FieldWithError
                    formik={formik}
                    type={fields[formRowItem].type}
                    formType={type}
                    data-testid={fields[formRowItem].dataTestId}
                    label={fields[formRowItem].label}
                    field={`${addressType}.${fields[formRowItem].name}`}
                    disabled={fields[formRowItem].disabled}
                    options={fields[formRowItem].options}
                    autoComplete={fields[formRowItem].autoComplete}
                  />
                ) : (
                  <FormSubRow
                    key={index}
                    formSubRow={formRowItem}
                    i18n={i18n}
                    formik={formik}
                    type={type}
                    addressType={addressType}
                  />
                ))}
            </Box>
          );
        }
      )}
    </Flex>
  );
};

interface AddressFormProps {
  formik: FormikProps<Checkout> | FormikProps<Profile>;
  formikValuesAddress: Address | undefined;
  formikErrorsAddress: FormikErrors<Address> | undefined;
  formikTouchedAddress: FormikTouched<Address> | undefined;
  type: "checkout" | "profile";
  addressType: "shippingAddress" | "billingAddress";
}

const AddressForm = ({
  formik,
  formikValuesAddress,
  formikErrorsAddress,
  formikTouchedAddress,
  type,
  addressType,
}: AddressFormProps): JSX.Element | null => {
  const showAutocompleteAddressNlForm = useAutompleteAddressNl(
    formik,
    formikValuesAddress,
    formikErrorsAddress,
    formikTouchedAddress,
    addressType
  );

  const { i18n } = useLingui();

  if (!formikValuesAddress) {
    return null;
  }

  return (
    <>
      {getFormRows(
        formikValuesAddress.country,
        showAutocompleteAddressNlForm
      ).map((formRow: FormRow, index: number) => (
        <FormRow
          key={index}
          formRow={formRow}
          formRowIndex={index}
          i18n={i18n}
          formik={formik}
          type={type}
          addressType={addressType}
        />
      ))}
    </>
  );
};

export default AddressForm;
